import { useEffect, useState } from "react";
import { ITimesheetModalProps } from "../types/modal";

import { useAppDispatch } from "../hooks/redux";
import { closeModalById } from "../features/modals";

import { useFormik } from "formik";
import axios from "axios";

import {
  Dialog,
  DialogActions,
  DialogTitle,
  Button,
  DialogContent,
  Stack,
  Typography,
  Alert,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

import { isTimesheetPeriodLocked } from "../helpers/timesheet";

import { renderTimeViewClock, TimePicker } from "@mui/x-date-pickers";
import moment from "moment";
import timesheetSchema, { TimesheetBase } from "../schemas/timesheet";

const TimesheetModal: React.FunctionComponent<ITimesheetModalProps> = ({
  id,
  callback,
  chairSchedule,
  user,
  userId,
  initialValues,
}) => {
  const dispatch = useAppDispatch();

  // Admins (acting on another user via userId) can edit any period.
  const isLocked = userId ? false : isTimesheetPeriodLocked(chairSchedule.date);

  const [isDeleting, setIsDeleting] = useState(false);
  const [duration, setDuration] = useState<moment.Duration>();

  const {
    values,
    handleChange,
    setFieldValue,
    handleSubmit,
    isSubmitting,
    errors,
    submitCount,
  } = useFormik<TimesheetBase>({
    initialValues: initialValues || {
      startedAt: chairSchedule.end ? chairSchedule.date : null,
      finishedAt: chairSchedule.end ? chairSchedule.end : null,
    },
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      axios
        .put(userId ? `/users/${userId}/timesheet` : "/users/timesheet", {
          _id: initialValues?._id,
          scheduleId: chairSchedule._id,
          ...values,
        })
        .then(() => {
          callback();
          handleClose();
        })
        .catch((err) => {
          console.log("err:", err);
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
    validationSchema: timesheetSchema,
  });

  useEffect(() => {
    if (values.startedAt && values.finishedAt) {
      setDuration(
        moment.duration(
          moment(values.finishedAt).diff(moment(values.startedAt))
        )
      );
    }
  }, [values.startedAt, values.finishedAt]);

  const handleClose = () => {
    dispatch(closeModalById(id));
  };

  return (
    <Dialog fullWidth={true} maxWidth="sm" open={true} onClose={handleClose}>
      <DialogTitle>
        {chairSchedule.chairName} {chairSchedule.clinic?.name} -{" "}
        {moment(chairSchedule.date).format("LL")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={4}>
          {isLocked && (
            <Alert severity="warning">
              Šī perioda stundas vairs nevar ziņot vai labot. Iepriekšējā mēneša
              stundas var labot tikai līdz tekošā mēneša 10. datumam.
            </Alert>
          )}
          <Stack spacing={2}>
            <Typography variant="h6">
              Darba stundas{" "}
              {duration && `(${duration.hours()}h ${duration.minutes()} min)`}
            </Typography>
            <Stack spacing={2} direction="row">
              <TimePicker
                label="Sākuma laiks"
                disabled={isLocked}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "standard",
                  },
                }}
                value={moment(values.startedAt)}
                viewRenderers={{
                  hours: renderTimeViewClock,
                  minutes: renderTimeViewClock,
                }}
                minTime={moment(values.startedAt).startOf("day")}
                maxTime={moment(values.finishedAt)}
                onChange={(newValue) => {
                  setFieldValue("startedAt", newValue);
                }}
                minutesStep={15}
              />
              <TimePicker
                label="Beigu laiks"
                disabled={isLocked}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "standard",
                  },
                }}
                viewRenderers={{
                  hours: renderTimeViewClock,
                  minutes: renderTimeViewClock,
                }}
                minTime={moment(values.startedAt)}
                maxTime={moment(values.finishedAt).endOf("day")}
                value={moment(values.finishedAt)}
                onChange={(newValue) => {
                  setFieldValue("finishedAt", newValue);
                }}
                minutesStep={15}
              />
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Aizvērt</Button>
        {/* {initialValues ? (
          <LoadingButton
            loading={isDeleting}
            onClick={() => {
              handleDelete(initialValues._id);
            }}
            color="error"
            variant="outlined"
          >
            Dzēst
          </LoadingButton>
        ) : null} */}
        <LoadingButton
          loading={isSubmitting}
          disabled={
            isDeleting ||
            isLocked ||
            moment().isBefore(moment(chairSchedule.date).startOf("day"))
          }
          onClick={() => {
            handleSubmit();
          }}
          variant="contained"
        >
          Saglabāt
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default TimesheetModal;
